import React, { useCallback } from 'react';
import { useDropzone, type Accept } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileText, Image } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './ui/Button';

interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  accept?: Accept;
  maxFiles?: number;
  maxSize?: number;
  label?: string;
  helperText?: string;
  error?: string;
  files?: File[];
  onRemove?: (index: number) => void;
  disabled?: boolean;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function FilePreview({ file, onRemove }: { file: File; onRemove: () => void }) {
  const { t } = useTranslation();
  const isImage = file.type.startsWith('image/');
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (isImage) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [file, isImage]);

  return (
    <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      {isImage && previewUrl ? (
        <img
          src={previewUrl}
          alt={file.name}
          className="h-10 w-10 rounded object-cover"
        />
      ) : (
        <div className="flex h-10 w-10 items-center justify-center rounded bg-primary-100">
          {isImage ? (
            <Image className="h-5 w-5 text-primary-600" aria-hidden="true" />
          ) : (
            <FileText className="h-5 w-5 text-primary-600" aria-hidden="true" />
          )}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-700">{file.name}</p>
        <p className="text-xs text-gray-400">{formatBytes(file.size)}</p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={onRemove}
        aria-label={t('fileUpload.removeFile', 'Remove {{name}}', { name: file.name })}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function FileUpload({
  onFilesSelected,
  accept = { 'image/*': [], 'application/pdf': [] },
  maxFiles = 5,
  maxSize = 5 * 1024 * 1024, // 5 MB
  label,
  helperText,
  error,
  files = [],
  onRemove,
  disabled,
}: FileUploadProps) {
  const { t } = useTranslation();
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onFilesSelected(acceptedFiles);
    },
    [onFilesSelected],
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept,
    maxFiles,
    maxSize,
    disabled,
  });

  const rejectionMessages = fileRejections.flatMap(({ errors }) =>
    errors.map((e) => e.message),
  );

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <span className="text-sm font-medium text-gray-700">{label}</span>
      )}

      <div
        {...getRootProps()}
        className={cn(
          'flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed p-8 transition-colors',
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-50',
          disabled && 'cursor-not-allowed opacity-60',
          error && 'border-red-400',
        )}
        aria-label={t('fileUpload.dropZoneLabel', 'File upload drop zone')}
      >
        <input {...getInputProps()} aria-label={t('fileUpload.inputLabel', 'File input')} />
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100">
          <Upload className="h-6 w-6 text-primary-600" aria-hidden="true" />
        </div>
        <div className="text-center">
          <p className="text-sm font-medium text-gray-700">
            {isDragActive ? t('fileUpload.dropHere', 'Drop files here') : t('fileUpload.clickOrDrag', 'Click or drag files to upload')}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {helperText ?? t('fileUpload.helperDefault', 'Up to {{maxFiles}} files, max {{maxSize}} each', { maxFiles, maxSize: formatBytes(maxSize) })}
          </p>
        </div>
      </div>

      {rejectionMessages.length > 0 && (
        <ul className="space-y-1">
          {rejectionMessages.map((msg, i) => (
            <li key={i} className="text-xs text-red-600" role="alert">
              {msg}
            </li>
          ))}
        </ul>
      )}

      {error && (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}

      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file, index) => (
            <FilePreview
              key={`${file.name}-${index}`}
              file={file}
              onRemove={() => onRemove?.(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
